import { useCallback, useMemo } from 'react';

/**
 * Given a css value, returns the displayed Value for the textinput,
 * the array of values top,right,bottom,left,
 * and the function to either toggle a specific corner, or update the global value
 */

export const useDetailedValues = (value?: string, placeholder?: string) => {
  const individualValues = (value ?? placeholder ?? '')
    .toString()
    .split(' ')
    .map((v) => v.replace(`px`, ``));
  const fallbackValue = individualValues.find((v) => v && v !== '0') || undefined;

  const values = useMemo(() => {
    if (individualValues.length === 1 || individualValues.every((v) => v === fallbackValue)) {
      return {
        top: undefined,
        right: undefined,
        bottom: undefined,
        left: undefined,
        all: parseFloat(individualValues[0] || '0'),
      };
    }

    return {
      top: parseFloat(individualValues[0] || '0'),
      right: parseFloat(individualValues[1] || '0'),
      bottom: parseFloat(individualValues[2] || '0'),
      left: parseFloat(individualValues[3] || '0'),
      all: undefined,
    };
  }, [fallbackValue, individualValues]);

  const toggleValue = useCallback(
    (type: 'top' | 'right' | 'bottom' | 'left' | 'all') => {
      if (type === 'all') {
        return values[type] ? '0px' : `${fallbackValue || 1}px`;
      }

      return ['top', 'right', 'bottom', 'left']
        .map((direction: 'top' | 'right' | 'bottom' | 'left') => {
          if (type === direction) {
            return values[direction] ? 0 : `${fallbackValue || 1}px`;
          }
          return `${values?.[direction] || 0}px`;
        })
        .join(' ');
    },
    [fallbackValue, values],
  );

  const updateValue = useCallback(
    (newValue: number) =>
      values.all || !fallbackValue || !/\d+/.test(fallbackValue ?? '')
        ? `${newValue}px`
        : ['top', 'right', 'bottom', 'left']
            .map((v: 'top' | 'right' | 'bottom' | 'left') => {
              return values[v] ? `${newValue}px` : '0px';
            })
            .join(' '),
    [values, fallbackValue],
  );
  return {
    isPlaceholder: !value && !!placeholder,
    displayedValue: parseFloat(value === '0px' ? '0' : fallbackValue?.replace('px', '') || '0'),
    values,
    toggleValue,
    updateValue,
  };
};
